import {Injectable} from '@angular/core';
import {TorchAPIConfiguration} from '../../../config/api.configuration';
import {HttpClient} from '@angular/common/http';
import {Burly} from 'kb-burly';
import {FileUploadResponse} from '../responses';
import {Observable} from 'rxjs';
import {SortDirection} from '@angular/material/sort';
import {ManyResponse} from '../../../responses';
import {UploadedFile} from '../models';
import {TorchUserService} from '../../@users';
import {SessionService} from '../../../../app/shared/services/session.service';

@Injectable({
  providedIn: 'root'
})
export class TorchFilesService {
  constructor(protected config: TorchAPIConfiguration,
              private httpClient: HttpClient,
              private torchUserService: TorchUserService,
              private sessionService: SessionService) {
  }

  public getFile(id: number | string): Observable<UploadedFile> {
    const endpoint = Burly(this.config.filesUrl)
      .addSegment(`/files/${id}`)
      .get;

    return this.httpClient.get<UploadedFile>(endpoint);
  }

  public getOptixFileDownloadURL(optixID: string): string {
    return Burly(this.config.filesUrl)
      .addSegment('/optix')
      .addSegment(`/download/${optixID}`)
      .get;
  }


  public getOptixFileViewURL(optixID: string): string {
    return Burly(this.config.filesUrl)
      .addSegment('/optix')
      .addSegment(`/download/${optixID}`)
      .get;
  }

  public uploadBase64(data: string, mimeType: string, fileName: string): Observable<FileUploadResponse> {
    const endpointURL = Burly(this.baseImageURL)
      .addSegment('/files')
      .addSegment('/upload')
      .addQuery('uploadedBy', this.sessionService.currentTorchUser.value.emailAddress)
      .addQuery('uploadType', 'base64')
      .get;

    return this.httpClient.post<FileUploadResponse>(endpointURL, {
      data,
      mimeType,
      fileName
    });
  }

  public deleteFile(file: UploadedFile) {
    const endpoint = Burly(this.config.filesUrl)
      .addSegment('/files')
      .addSegment('/delete')
      .addSegment('/' + file.fileName)
      .get;

    return this.httpClient.delete(endpoint);
  }

  public deleteFileByName(fileName: string) {
    const endpoint = Burly(this.config.filesUrl)
      .addSegment('/files')
      .addSegment('/delete')
      .addSegment('/' + fileName)
      .get;

    return this.httpClient.delete(endpoint);
  }

  public uploadFile(formData: FormData) {
    const endpoint = Burly(this.config.filesUrl)
      .addSegment('/files')
      .addSegment('/upload')
      .addQuery('uploadedBy', this.sessionService.currentTorchUser.value.emailAddress)
      .get;

    return this.httpClient.post(endpoint, formData);
  }

  public queryFiles(pageSize: number = 15,
                    currentPage: number = 0,
                    sortDirection?: SortDirection,
                    sortBy?: string,
                    filter?: string | string[],
                    orFilter?: string | string[]): Observable<ManyResponse<UploadedFile>> {

    let finalURL;
    const endpoint = Burly(this.config.filesUrl)
      .addSegment('/files')
      .addQuery('page', (currentPage + 1))
      .addQuery('limit', pageSize);

    if (sortDirection && sortBy) {
      endpoint.addQuery('sort', `${sortBy},${sortDirection.toUpperCase()}`);
    }

    finalURL = endpoint.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach(filterValue => {
            finalURL = `${finalURL}&filter=${encodeURIComponent(filterValue)}`;
          });
        }
      } else {
        finalURL = `${finalURL}&or=${encodeURIComponent(filter)}`;
      }
    }

    if (orFilter) {
      if (orFilter instanceof Array) {
        if (orFilter.length > 0) {
          orFilter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}&filter=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&or=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}&or=${encodeURIComponent(orFilter)}`;
      }
    }

    return this.httpClient.get<ManyResponse<UploadedFile>>(finalURL);
  }

  public getURL(forFileName: string) {
    return `${this.baseImageURL}/files/download/${forFileName.replace(/ /g, '%20')}`;
  }

  public get baseImageURL(): string {
    return this.config.filesUrl;
  }

  get tinyMCEUploadURL(): string {
    return Burly(this.config.filesUrl)
      .addSegment('/files/upload')
      .addQuery('uploadType', 'tinyMCE')
      .addQuery('uploadedBy', this.sessionService.currentTorchUser.value.emailAddress)
      .get;
  }

  /**
   * Returns a URL to a 1x1 transparent PNG
   */
  get authImageURL(): string {
    return Burly(this.config.filesUrl)
      .addSegment('/files/auth')
      .get;
  }
}
